import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Bell, BellRing, CheckCircle, Settings, Smartphone, Download } from 'lucide-react';
import { isPWAInstalled } from '@/lib/pwa';

/**
 * The "Don't miss urgent alerts" onboarding.
 *
 * Installing the app is offered first when it is not already installed, because mobile
 * browsers only deliver notifications reliably to an installed web app:
 *
 *  - iOS: `Notification.requestPermission()` does not even prompt unless the site was
 *    added to the Home Screen and is running as a web app.
 *  - Android: Chrome owns the site's notification channel, and can create it at a low
 *    importance — which means the alert makes a sound and lands in the shade but never
 *    pops up over other apps. An installed web app gets its own dedicated channel, which
 *    is the most reliable way to receive a heads-up alert. Chrome can also revoke the
 *    permission of a site it judges to have low engagement.
 *
 * "Not now" still enables notifications, so nothing is gated; the install is strongly
 * recommended, not required.
 */
export function NotificationOnboardingDialog({ open, onOpenChange, onEnable, onDismiss, onInstall, onShowInstructions, permission }) {
  const needsInstall = !isPWAInstalled();
  // Once the browser has blocked notifications, requestPermission() resolves
  // 'denied' without prompting again — so the only useful action is to send
  // the user to their browser settings (KAN-132).
  const isBlocked = permission === 'denied';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center">
            <BellRing className="h-8 w-8 text-amber-600" />
          </div>
          <DialogTitle className="text-center text-xl font-['Manrope']">
            Don't miss urgent alerts
          </DialogTitle>
          <DialogDescription className="text-center text-base pt-2">
            Some opportunities are time-critical. Please allow notifications so alerts can reach you promptly.
          </DialogDescription>
        </DialogHeader>
        {needsInstall && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
            <p className="font-medium">Install ExpressSub first</p>
            <p className="mt-1">
              Your phone only delivers notifications reliably to the app installed on your
              home screen. Without it the browser can put your alerts on a low-priority
              channel — they make a sound, but they never pop up over your other apps.
            </p>
          </div>
        )}
        {isBlocked && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <p className="font-medium">Notifications are blocked</p>
            <p className="mt-1">
              Your browser is blocking notifications for this site. Turn them back on in
              your browser settings to receive alerts for new positions.
            </p>
          </div>
        )}
        <div className="flex flex-col gap-3 pt-4">
          {isBlocked && onShowInstructions && (
            <Button
              onClick={onShowInstructions}
              className="w-full bg-amber-600 hover:bg-amber-700 h-12 text-base"
              data-testid="notification-settings-btn"
            >
              <Settings className="mr-2 h-5 w-5" />
              Show me how to enable them
            </Button>
          )}
          {needsInstall && onInstall && (
            <Button
              onClick={onInstall}
              className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-base"
              data-testid="install-app-btn"
            >
              <Download className="mr-2 h-5 w-5" />
              Install ExpressSub app
            </Button>
          )}
          {!isBlocked && (
            <Button
              onClick={onEnable}
              className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base"
              data-testid="enable-notifications-btn"
            >
              <Bell className="mr-2 h-5 w-5" />
              Enable Notifications
            </Button>
          )}
          <Button 
            variant="ghost" 
            onClick={onDismiss}
            className="text-slate-500 hover:text-slate-700"
            data-testid="dismiss-notifications-btn"
          >
            Not now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function NotificationInstructionsDialog({ open, onOpenChange, platform, onClose }) {
  const getTitle = () => {
    if (platform === 'ios') return 'iOS Settings';
    if (platform === 'android') return 'Android Settings';
    return 'Notification Settings';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
            <Settings className="h-8 w-8 text-emerald-600" />
          </div>
          <DialogTitle className="text-center text-xl font-['Manrope']">
            {getTitle()}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 pt-2">
          {platform === 'ios' && (
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="font-medium text-slate-700 mb-3">On the next screen:</p>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Turn <strong>Allow Notifications</strong> ON</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Enable <strong>Sounds</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Enable <strong>Time-Sensitive Alerts</strong> (if shown)</span>
                </li>
              </ul>
            </div>
          )}
          {platform === 'android' && (
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="font-medium text-slate-700 mb-3">On the next screen:</p>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Turn <strong>notifications ON</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Set importance to <strong>High</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Allow <strong>sound and vibration</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Allow alerts during <strong>Do Not Disturb</strong> (if available)</span>
                </li>
              </ul>
            </div>
          )}
          {platform !== 'ios' && platform !== 'android' && (
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="font-medium text-slate-700 mb-3">For best results:</p>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Keep this browser tab open or pinned</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Enable browser notification sounds in your system settings</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Make sure "Do Not Disturb" is off when expecting alerts</span>
                </li>
              </ul>
            </div>
          )}
          
          <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <Smartphone className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-700">
              These settings help ensure you never miss an urgent job opportunity
            </p>
          </div>
        </div>
        
        <DialogFooter className="pt-4">
          <Button 
            onClick={onClose}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="got-it-btn"
          >
            Got it!
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
